import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Button, Tabs, TabsContent, TabsList, TabsTrigger, Text } from "@ferrivia/ui";
import { useFormatters, useMarket, useTranslation } from "@ferrivia/i18n";
import { useNavigate, useOutletContext, useParams } from "react-router";
import type {
  ChatMessage,
  ChatThread,
  ChatThreadStatus,
  PatientAction,
  ReplyPreview,
  ScheduledMessage,
  SendMessageRequest,
  User,
} from "@ferrivia/api-contract";
import { useApi, useTracker } from "@ferrivia/auth-web";
import { Composer, useChatPoll, useChatThread, useStickToBottom, type ComposerHandle } from "@ferrivia/chat-ui";
import { ArrowLeft, ChevronRight, Clock } from "lucide-react";
import { currentCheckIn } from "../../../actions/plan";
import { ActionCheckIn } from "../../../components/ActionCheckIn";
import { ActionDetail } from "../../../components/ActionDetail";
import { ActionDrawer, type ActionSourceMessage } from "../../../components/ActionDrawer";
import { PendingScheduledMessage, focusScheduledMessage } from "../../../components/PendingScheduledMessage";
import { ScheduleDialog } from "../../../components/ScheduleDialog";
import { TimelineFeed } from "../../../components/TimelineFeed";
import type { FeedEntry } from "../../../components/TimelineFeed/types";
import { notifyChatSummariesStale } from "../../../hooks/chatSummaryBus";
import { useCustomerActions, type CustomerActions } from "../../../hooks/useCustomerActions";
import { patientDisplayName, usePatient } from "../../../hooks/usePatients";
import { usePatientTimeline, type PatientTimelineState } from "../../../hooks/usePatientTimeline";
import { useScheduledMessages, type ScheduledMessages } from "../../../hooks/useScheduledMessages";
import { useStaffProfile } from "../../../hooks/useStaffProfile";
import { DetailsPane } from "./DetailsPane";
import { LabsPane } from "./LabsPane";
import { JourneyPane } from "./JourneyPane";
import { NotesPane } from "./NotesPane";
import { PlanStrip } from "./PlanStrip";
import { ScreenerPane } from "./ScreenerPane";
import s from "./CustomerWorkspace.module.css";

type MobileView = "feed" | "context";
type WorkspaceTab = "labs" | "details" | "journey" | "notes" | "screener";
const DEFAULT_TAB: WorkspaceTab = "labs";

type DrawerState = {
  mode: "create" | "change" | "detail" | "checkIn";
  actionId?: string;
  sourceEntry?: ActionSourceMessage;
};

// Identity only — who they are and since when. Role is constant for everyone
// this screen can open (a clinician reaches it from the client queue), and the
// sign-in method is account plumbing, not something staff act on mid-thread;
// both were costing a header line on a phone that the conversation needs.
function formatMeta(
  state: Extract<ReturnType<typeof usePatient>, { status: "success" }>,
  tag: string,
  noEmail: string,
  joinedLabel: (date: string) => string,
): string {
  const { patient } = state;
  const joined = new Intl.DateTimeFormat(tag, { year: "numeric", month: "short", day: "numeric" }).format(
    new Date(patient.createdAt),
  );
  return [patient.email ?? noEmail, joinedLabel(joined)].join(" · ");
}

// Keyed by patientId at the call site so switching customers in the queue
// remounts this instead of leaking one patient's chat state into another's —
// unlike usePatient/usePatientTimeline, useChatThread has no id-driven reset
// of its own.
function ThreadStatusAction({ patientId, className }: { patientId: string; className?: string }) {
  const { t } = useTranslation("clinic");
  const api = useApi();
  const [status, setStatus] = useState<ChatThreadStatus | null>(null);

  // Polled, not fetched once — a client reply reopens a resolved thread on
  // the backend (see reopenIfResolved), and that happens outside this
  // session entirely, so there's no local event to react to. Only polling
  // catches it.
  useChatPoll(async () => {
    const thread = await api.clinicChat.thread(patientId);
    setStatus(thread.status);
  });

  async function toggle() {
    if (!status) {
      return;
    }
    const next = status === "open" ? "resolved" : "open";
    const updated = await api.clinicChat.setStatus(patientId, next);
    setStatus(updated.status);
    notifyChatSummariesStale();
  }

  return (
    <Button variant="secondary" className={className} onClick={toggle} disabled={!status}>
      {status === "resolved" ? t("customerWorkspace.reopen", "Reopen") : t("customerWorkspace.resolve", "Resolve")}
    </Button>
  );
}

interface ChatFeedPaneProps {
  patientId: string;
  patientName: string;
  timelineState: PatientTimelineState;
  staffUserId: string | undefined;
  staffName: string;
  clientName: string;
  scheduled: ScheduledMessages;
  actions: CustomerActions;
  pendingOpen: boolean;
  onTogglePending: () => void;
  onOpenLabs: (entryKind: FeedEntry["kind"]) => void;
  onCreateAction: (source: ActionSourceMessage) => void;
  onOpenAction: (actionId: string) => void;
}

type ScheduleDialogTarget = { kind: "create"; text: string; clear: () => void } | { kind: "edit"; message: ScheduledMessage };

function toReplyPreview(msg: Pick<ChatMessage, "id" | "authorType" | "authorName" | "body" | "redactedAt">): ReplyPreview {
  return {
    id: msg.id,
    authorType: msg.authorType,
    authorName: msg.authorName,
    body: msg.body,
    redactedAt: msg.redactedAt,
  };
}

// Also keyed by patientId — same reason as ThreadStatusAction above.
function ChatFeedPane({
  patientId,
  patientName,
  timelineState,
  staffUserId,
  staffName,
  clientName,
  scheduled,
  actions,
  pendingOpen,
  onTogglePending,
  onOpenLabs,
  onCreateAction,
  onOpenAction,
}: ChatFeedPaneProps) {
  const { t } = useTranslation("clinic");
  const api = useApi();
  const tracker = useTracker();
  const [threadId, setThreadId] = useState<string | null>(null);
  const pendingListId = useId();

  const transport = useMemo(
    () => ({
      listMessages: (after?: number) => api.clinicChat.messages(patientId, after),
      send: (input: SendMessageRequest) =>
        api.clinicChat.send(patientId, input).then((message) => {
          notifyChatSummariesStale();
          return message;
        }),
    }),
    [api, patientId],
  );
  const { messages, outbox, send, retry, refresh, replyTo, setReplyTo } = useChatThread({ transport });
  useChatPoll(refresh);

  // Nothing else in the app ever marks a thread read on the staff side, so
  // without this the queue's unread badge can never clear — it isn't tied
  // to whether staff actually looked at the thread, just to this call.
  // Staff viewing the pane counts as reading everything currently loaded.
  const lastMarkedSeqRef = useRef(0);
  useEffect(() => {
    const latestSeq = messages.at(-1)?.seq ?? 0;
    if (latestSeq > lastMarkedSeqRef.current) {
      lastMarkedSeqRef.current = latestSeq;
      api.clinicChat
        .markRead(patientId, latestSeq)
        .then(() => notifyChatSummariesStale())
        .catch(() => {});
    }
  }, [api, patientId, messages]);

  // A client message holds a scheduled one and a sweep sends one; both land
  // as a change in the thread, so the chat poll doubles as the refresh tick.
  const lastSeq = messages.at(-1)?.seq ?? 0;
  const lastSeqRef = useRef(lastSeq);
  const refreshScheduled = scheduled.refresh;
  useEffect(() => {
    if (lastSeqRef.current !== lastSeq) {
      lastSeqRef.current = lastSeq;
      void refreshScheduled();
    }
  }, [lastSeq, refreshScheduled]);

  const [dialog, setDialog] = useState<ScheduleDialogTarget | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.clinicChat
      .thread(patientId)
      .then((thread: ChatThread) => {
        if (!cancelled) {
          setThreadId(thread.id);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [api, patientId]);

  useEffect(() => {
    if (threadId && staffUserId) {
      tracker.track("clinic.chat_thread_opened", { viewed_by: staffUserId, thread_id: threadId });
    }
    // Fire once per patient (this pane is remounted per patientId), not on every tracker/staffUserId identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  function sendDraft(text: string) {
    send({ v: 1, blocks: [{ type: "text", text }] });
  }

  // Focused here rather than by the composer's own effect: TimelineFeed calls
  // this from inside the swipe's pointerup, and a touch browser only raises
  // its keyboard for a focus() made during that gesture.
  const composerRef = useRef<ComposerHandle>(null);

  function handleReply(msg: Pick<ChatMessage, "id" | "authorType" | "authorName" | "body" | "redactedAt">) {
    setReplyTo(msg as ChatMessage);
    composerRef.current?.focus();
  }

  const feedEntries: FeedEntry[] = useMemo(() => {
    const fromTimeline: FeedEntry[] = timelineState.status === "success" ? timelineState.entries : [];
    const fromMessages: FeedEntry[] = messages.map((message) => ({
      id: message.id,
      kind: "message",
      occurredAt: message.createdAt,
      authorType: message.authorType,
      authorName: message.authorName,
      body: message.body,
      replyTo: message.replyTo,
    }));
    const fromOutbox: FeedEntry[] = outbox.map((entry) => ({
      id: entry.clientMessageId,
      kind: "message",
      occurredAt: entry.createdAt,
      authorType: "staff",
      authorName: null,
      body: entry.body,
      replyTo: entry.replyTo,
      delivery: { state: entry.state, onRetry: () => retry(entry.clientMessageId) },
    }));
    const byId = new Map(actions.actions.map((action) => [action.id, action]));
    const fromActions: FeedEntry[] = actions.actions.map((action) => ({
      id: `action:${action.id}`,
      kind: "action",
      occurredAt: action.createdAt,
      action,
      previous: action.replacesId ? byId.get(action.replacesId) : undefined,
    }));
    const fromCheckIns: FeedEntry[] = actions.checkIns.flatMap((checkIn) => {
      const action = byId.get(checkIn.actionId);
      return action
        ? [
            {
              id: `action_check_in:${checkIn.id}`,
              kind: "action_check_in" as const,
              occurredAt: checkIn.createdAt,
              checkIn,
              action,
            },
          ]
        : [];
    });
    return [...fromTimeline, ...fromMessages, ...fromOutbox, ...fromActions, ...fromCheckIns].sort(
      (a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime(),
    );
  }, [timelineState, messages, outbox, retry, actions.actions, actions.checkIns]);

  const actionsByMessageId = useMemo(() => {
    const replaced = new Set(actions.actions.map((action) => action.replacesId));
    const map = new Map<string, PatientAction[]>();
    for (const action of actions.actions) {
      if (action.sourceMessageId && !replaced.has(action.id)) {
        map.set(action.sourceMessageId, [...(map.get(action.sourceMessageId) ?? []), action]);
      }
    }
    return map;
  }, [actions.actions]);

  function handleCreateAction(entry: Extract<FeedEntry, { kind: "message" }>) {
    onCreateAction({
      id: entry.id,
      senderName:
        entry.authorType === "staff" ? (entry.authorName ?? t("timelineFeed.fallbackStaffName", "Staff")) : patientName,
      occurredAt: entry.occurredAt,
      body: entry.body,
    });
  }

  const feedScrollRef = useRef<HTMLDivElement>(null);
  useStickToBottom(feedScrollRef, feedEntries, []);

  return (
    <>
      <div className={s.feedScroll} ref={feedScrollRef}>
        {timelineState.status === "loading" && (
          <Text as="p" size="sm" className={s.empty}>
            {t("customerWorkspace.loadingTimeline", "Loading timeline…")}
          </Text>
        )}
        {timelineState.status === "error" && (
          <Text as="p" size="sm" className={s.empty}>
            {t("customerWorkspace.timelineLoadError", "Couldn't load the timeline.")}
          </Text>
        )}
        {timelineState.status === "success" && feedEntries.length === 0 && (
          <Text as="p" size="sm" className={s.empty}>
            {t("customerWorkspace.noActivity", "No activity yet.")}
          </Text>
        )}
        {timelineState.status === "success" && feedEntries.length > 0 && (
          <TimelineFeed
            entries={feedEntries}
            patientName={patientName}
            onReply={handleReply}
            onOpenLabs={onOpenLabs}
            onCreateAction={handleCreateAction}
            actionsByMessageId={actionsByMessageId}
            onOpenAction={onOpenAction}
          />
        )}
      </div>
      {scheduled.messages.length > 0 ? (
        <div className={pendingOpen ? `${s.pending} ${s.pendingOpen}` : s.pending}>
          <button
            type="button"
            className={s.pendingHeader}
            aria-expanded={pendingOpen}
            aria-controls={pendingListId}
            onClick={onTogglePending}
          >
            <ChevronRight aria-hidden size={18} className={s.pendingChevron} />
            <Clock aria-hidden size={14} />
            {t("customerWorkspace.scheduled.sectionLabel", "Scheduled · not sent yet")}
            <span className={s.pendingCount}>{scheduled.messages.length}</span>
          </button>
          <div id={pendingListId} hidden={!pendingOpen}>
            {scheduled.messages.map((message) => (
              <PendingScheduledMessage
                key={message.id}
                message={message}
                clientName={clientName}
                staffUserId={staffUserId}
                onEdit={(m) => setDialog({ kind: "edit", message: m })}
                onSendNow={(m) => scheduled.sendNow(m.id)}
                onCancel={(m) => scheduled.cancel(m.id)}
              />
            ))}
          </div>
        </div>
      ) : null}
      <div className={s.composerSafeArea}>
        <Composer
          ref={composerRef}
          placeholder={t("customerWorkspace.composerPlaceholder", "Write a message…")}
          onSend={sendDraft}
          sendLabel={t("customerWorkspace.send", "Send")}
          replyTo={replyTo ? toReplyPreview(replyTo) : null}
          onClearReply={() => setReplyTo(null)}
          renderAccessory={({ text, clear }) => (
            <button
              type="button"
              className={s.sendLater}
              disabled={text.trim() === ""}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => setDialog({ kind: "create", text: text.trim(), clear })}
              aria-label={t("customerWorkspace.scheduled.sendLater", "Send later")}
              title={t("customerWorkspace.scheduled.sendLater", "Send later")}
            >
              <Clock aria-hidden size={18} />
            </button>
          )}
        />
      </div>
      {dialog ? (
        <ScheduleDialog
          open
          onOpenChange={(open) => {
            if (!open) {
              setDialog(null);
            }
          }}
          existing={dialog.kind === "edit" ? dialog.message : undefined}
          initialText={dialog.kind === "create" ? dialog.text : undefined}
          clientName={clientName}
          staffName={staffName}
          onSubmit={async (req) => {
            if (dialog.kind === "edit") {
              await scheduled.update(dialog.message.id, req);
            } else {
              await scheduled.schedule(req);
              dialog.clear();
            }
          }}
        />
      ) : null}
    </>
  );
}

export function CustomerWorkspace() {
  const { t } = useTranslation("clinic");
  const market = useMarket();
  const { tag } = useFormatters();
  const { customerId } = useParams();
  const navigate = useNavigate();
  const tracker = useTracker();
  const staffUser = useOutletContext<User | undefined>();
  const [mobileView, setMobileView] = useState<MobileView>("feed");
  // Collapsed by default: an open list crowds out the feed, especially on a phone.
  const [pendingOpen, setPendingOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<WorkspaceTab>(DEFAULT_TAB);
  const patientState = usePatient(customerId);
  const timelineState = usePatientTimeline(customerId);
  const scheduled = useScheduledMessages(customerId);
  const actions = useCustomerActions(customerId);
  const staffProfile = useStaffProfile();
  const [contextEl, setContextEl] = useState<HTMLElement | null>(null);
  const [drawer, setDrawer] = useState<DrawerState | null>(null);

  // A ref, not a dep: tracker's identity can change but this must still
  // fire exactly once per mount, not once per identity change.
  const trackerRef = useRef(tracker);
  trackerRef.current = tracker;

  useEffect(() => {
    trackerRef.current.track("clinic.workspace_tab_selected", { tab: DEFAULT_TAB });
  }, []);

  useEffect(() => {
    setDrawer(null);
  }, [customerId]);

  function handleTabChange(tab: string) {
    setActiveTab(tab as WorkspaceTab);
    tracker.track("clinic.workspace_tab_selected", { tab: tab as WorkspaceTab });
  }

  // A timeline entry that reports a result carries no values — reading them
  // is the Labs tab's job, which is also where that read is logged as its own
  // asiakastietolaki disclosure. This is the move between the two: it selects
  // the tab and, below 960px where the panes are alternatives rather than
  // columns, brings the context pane forward.
  function handleOpenLabs(entryKind: FeedEntry["kind"]) {
    setActiveTab("labs");
    setMobileView("context");
    tracker.track("clinic.timeline_entry_link_followed", {
      kind: entryKind,
      target: "labs",
    });
  }

  function handleOpenScheduled(message: ScheduledMessage) {
    tracker.track("clinic.plan_row_opened", { kind: "scheduled_message" });
    setMobileView("feed");
    setPendingOpen(true);
    requestAnimationFrame(() => focusScheduledMessage(message.id));
  }

  function openDrawer(next: DrawerState) {
    setDrawer(next);
    setMobileView("context");
  }

  function handleOpenAction(actionId: string) {
    openDrawer({ mode: "detail", actionId });
  }

  function handleOpenSourceMessage(messageId: string) {
    setMobileView("feed");
    requestAnimationFrame(() => {
      document.querySelector(`[data-message-id="${messageId}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }

  function handleOpenActionFromPlan(actionId: string) {
    tracker.track("clinic.plan_row_opened", { kind: "action" });
    handleOpenAction(actionId);
  }

  const drawerAction = drawer?.actionId ? (actions.actions.find((a) => a.id === drawer.actionId) ?? null) : null;

  const unnamedPatient = t("queueList.unnamedPatient", "Unnamed patient");
  const isLoaded = patientState.status === "success";
  const heading = isLoaded ? patientDisplayName(patientState.patient, unnamedPatient) : null;
  // Fallback used only for ChatFeedPane's internal sender-label logic while loading —
  // never rendered in the header itself.
  const patientNameForFeed = isLoaded
    ? patientDisplayName(patientState.patient, unnamedPatient)
    : t("customerWorkspace.fallbackPatientName", "Patient");

  const clientFirstName =
    (isLoaded ? patientState.patient.givenName : null) ??
    t("customerWorkspace.scheduled.clientFallback", "The client");

  return (
    <div className={s.root} data-mobile-view={mobileView}>
      <header className={s.header}>
        <button type="button" className={s.back} onClick={() => navigate("/queue")}>
          <ArrowLeft aria-hidden size={22} strokeWidth={1.75} />
          <span className={s.srOnly}>{t("customerWorkspace.backToQueue", "Back to queue")}</span>
        </button>
        <span className={s.avatar} aria-hidden="true" />
        <div className={s.identity}>
          <Text as="h1" size="xl" weight="bold" className={s.name}>
            {heading ?? <span className={s.skeleton} style={{ width: "9rem" }} aria-hidden="true" />}
          </Text>
          <Text as="p" size="sm" className={s.meta}>
            {isLoaded ? (
              formatMeta(
                patientState,
                tag,
                t("navRail.noEmail", "No email"),
                (joined) => t("customerWorkspace.joinedOn", { date: joined, defaultValue: "joined {{date}}" }),
              )
            ) : (
              <span className={s.skeleton} style={{ width: "14rem" }} aria-hidden="true" />
            )}
          </Text>
          {!isLoaded && <span className={s.srOnly}>{t("customerWorkspace.loadingPatient", "Loading patient…")}</span>}
        </div>
        <div className={s.actions}>
          {customerId ? (
            <ThreadStatusAction key={customerId} patientId={customerId} className={s.resolve} />
          ) : null}
        </div>

        {/* Inside the header, not a row of its own: below 960px this is the
            second header row and it carries Resolve alongside it, which is
            what frees the whole app-bar row the name now sits on. Above
            960px the switch is hidden and Resolve falls back to the right
            end of the single header row. */}
        <div className={s.switch} role="group" aria-label={t("customerWorkspace.viewSwitchLabel", "Workspace view")}>
          <button
            type="button"
            className={mobileView === "feed" ? `${s.switchButton} ${s.switchActive}` : s.switchButton}
            aria-pressed={mobileView === "feed"}
            onClick={() => setMobileView("feed")}
          >
            {t("customerWorkspace.viewFeed", "Feed")}
          </button>
          <button
            type="button"
            className={
              mobileView === "context" ? `${s.switchButton} ${s.switchActive}` : s.switchButton
            }
            aria-pressed={mobileView === "context"}
            onClick={() => setMobileView("context")}
          >
            {t("customerWorkspace.viewContext", "Context")}
          </button>
        </div>
      </header>

      <section className={s.feed} aria-label={t("customerWorkspace.feedRegionLabel", "Feed")}>
        {customerId ? (
          <ChatFeedPane
            key={customerId}
            patientId={customerId}
            patientName={patientNameForFeed}
            timelineState={timelineState}
            staffUserId={staffUser?.id}
            staffName={staffUser?.preferredName ?? staffUser?.email ?? ""}
            clientName={clientFirstName}
            scheduled={scheduled}
            actions={actions}
            pendingOpen={pendingOpen}
            onTogglePending={() => setPendingOpen((open) => !open)}
            onOpenLabs={handleOpenLabs}
            onCreateAction={(sourceEntry) => openDrawer({ mode: "create", sourceEntry })}
            onOpenAction={handleOpenAction}
          />
        ) : null}
      </section>

      <section
        ref={setContextEl}
        className={s.context}
        aria-label={t("customerWorkspace.contextRegionLabel", "Context")}
      >
        <PlanStrip
          actions={actions.actions}
          checkIns={actions.checkIns}
          messages={scheduled.messages}
          clientName={clientFirstName}
          onAdd={() => openDrawer({ mode: "create" })}
          onOpenAction={handleOpenActionFromPlan}
          onOpenScheduled={handleOpenScheduled}
        />
        <Tabs value={activeTab} onValueChange={handleTabChange} className={s.tabs}>
          <TabsList className={s.tabsList}>
            <TabsTrigger value="labs">{t("customerWorkspace.tabLabs", "Labs")}</TabsTrigger>
            <TabsTrigger value="details">{t("customerWorkspace.tabDetails", "Details")}</TabsTrigger>
            {market === "fi" && <TabsTrigger value="journey">{t("customerWorkspace.tabJourney", "Journey")}</TabsTrigger>}
            <TabsTrigger value="notes">{t("customerWorkspace.tabNotes", "Notes")}</TabsTrigger>
            <TabsTrigger value="screener">{t("customerWorkspace.tabScreener", "Screener")}</TabsTrigger>
          </TabsList>
          <TabsContent value="labs" className={s.pane}>
            {customerId ? <LabsPane key={customerId} customerId={customerId} /> : null}
          </TabsContent>
          <TabsContent value="details" className={s.pane}>
            {customerId ? <DetailsPane key={customerId} customerId={customerId} /> : null}
          </TabsContent>
          {market === "fi" && (
            <TabsContent value="journey" className={s.pane}>
              {customerId ? (
                <JourneyPane
                  key={customerId}
                  customerId={customerId}
                  actions={actions.actions}
                  checkIns={actions.checkIns}
                  timelineEntries={timelineState.status === "success" ? timelineState.entries : []}
                  onOpenLabs={handleOpenLabs}
                  onOpenAction={handleOpenAction}
                />
              ) : null}
            </TabsContent>
          )}
          <TabsContent value="notes" className={s.pane}>
            {customerId ? <NotesPane key={customerId} customerId={customerId} /> : null}
          </TabsContent>
          <TabsContent value="screener" className={s.pane}>
            {customerId ? (
              <ScreenerPane key={customerId} customerId={customerId} staffUserId={staffUser?.id} />
            ) : null}
          </TabsContent>
        </Tabs>
        {drawer?.mode === "create" ? (
          <ActionDrawer
            container={contextEl}
            origin={drawer.sourceEntry ? "message" : "plan"}
            clientName={clientFirstName}
            staff={staffProfile}
            sourceMessage={drawer.sourceEntry}
            onClose={() => setDrawer(null)}
            onSave={actions.add}
          />
        ) : null}
        {drawerAction && drawer?.mode === "change" ? (
          <ActionDrawer
            key={drawerAction.id}
            container={contextEl}
            origin="change"
            clientName={clientFirstName}
            staff={staffProfile}
            replaces={drawerAction}
            replacesCheckIn={currentCheckIn(drawerAction, actions.checkIns)}
            onClose={() => setDrawer(null)}
            onSave={actions.add}
          />
        ) : null}
        {drawerAction && drawer?.mode === "detail" ? (
          <ActionDetail
            key={drawerAction.id}
            container={contextEl}
            action={drawerAction}
            actions={actions.actions}
            checkIns={actions.checkIns}
            clientName={clientFirstName}
            onClose={() => setDrawer(null)}
            onChange={() => openDrawer({ mode: "change", actionId: drawerAction.id })}
            onCheckIn={() => openDrawer({ mode: "checkIn", actionId: drawerAction.id })}
            onOpenMessage={handleOpenSourceMessage}
          />
        ) : null}
        {drawerAction && drawer?.mode === "checkIn" ? (
          <ActionCheckIn
            key={drawerAction.id}
            container={contextEl}
            action={drawerAction}
            staff={staffProfile}
            onClose={() => setDrawer(null)}
            onChooseChange={() => openDrawer({ mode: "change", actionId: drawerAction.id })}
            onSave={(input) => actions.addCheckIn(drawerAction.id, input)}
          />
        ) : null}
      </section>
    </div>
  );
}
